import type { ReactNode } from 'react';
import { views } from '@/copy';
import VisualTooltip from './VisualTooltip';
import type { TableTitleModel } from '../types';

const MemberList = ({
    members,
    preserveCase,
}: {
    members: string[];
    preserveCase?: boolean;
}) => (
    <ul className="flex max-h-60 flex-col gap-0.5 overflow-y-auto">
        {members.map((name, i) => (
            <li
                key={`${i}-${name}`}
                className={preserveCase ? undefined : 'capitalize'}
            >
                {name}
            </li>
        ))}
    </ul>
);

/** The underlined ` · ` suffix that reveals the full list on hover/focus. */
const ListTooltip = ({
    members,
    preserveCase,
    children,
}: {
    members: string[];
    preserveCase?: boolean;
    children: ReactNode;
}) => (
    <VisualTooltip
        content={<MemberList members={members} preserveCase={preserveCase} />}
    >
        <span
            tabIndex={0}
            // ring-inset: a normal ring is clipped by the truncating title span.
            className="-mx-0.5 cursor-default rounded-sm px-0.5 underline underline-offset-2 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        >
            {children}
        </span>
    </VisualTooltip>
);

const InlineTableTitle = ({
    custom,
    parent,
    noun,
    members,
    preserveMemberCase,
    excluded,
    lens,
    lifeCycle,
    price,
}: TableTitleModel) => {
    const stages = lifeCycle?.length ? lifeCycle.join(', ') : null;
    const excludedCount = excluded?.length ?? 0;
    const pricePoints = price?.length
        ? price.map((p) => p.toLowerCase()).join(', ')
        : null;
    // Case the names only (CSS): "and N more" must stay lowercase.
    const [lead, ...rest] = members ?? [];
    const lineup = lead ? (
        <>
            <span className={preserveMemberCase ? undefined : 'capitalize'}>
                {lead}
            </span>
            {rest.length > 0 && (
                <>
                    {' and '}
                    <ListTooltip
                        members={members!}
                        preserveCase={preserveMemberCase}
                    >
                        {rest.length} more
                    </ListTooltip>
                </>
            )}
        </>
    ) : null;
    return (
        <span className="text-xs font-semibold text-foreground truncate">
            {custom ? (
                // Verbatim: the user's own casing.
                <span className="normal-case">{custom}</span>
            ) : parent ? (
                <>
                    <span className="capitalize">{parent}</span> {noun}
                    {lineup && (
                        <>
                            {' · '}
                            {lineup}
                        </>
                    )}
                </>
            ) : (
                (lineup ?? <span className="capitalize">{noun}</span>)
            )}
            {lens && (
                <>
                    {' · '}
                    <span>{lens}</span>
                </>
            )}
            {stages && (
                <>
                    {' · '}
                    <span className="capitalize">{stages}</span>
                </>
            )}
            {pricePoints && (
                <>
                    {' · '}
                    <span>{pricePoints}</span>
                </>
            )}
            {excludedCount > 0 && (
                <>
                    {' · '}
                    <ListTooltip members={excluded!} preserveCase>
                        {views.assistantVisuals.excludedCount(excludedCount)}
                    </ListTooltip>
                </>
            )}
        </span>
    );
};

export default InlineTableTitle;
